import { useMutation } from '@tanstack/react-query'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { pathDelmiter, Status, URLs } from '../../Constants/Consts'
import { setSelectedDD01, setSelectedInspectionTask, updateCheckListItem, setSelectedPath, setSelectedDD02, setSelectedModelNumber } from '../../store/SequenceObject'
import { RootState } from '../../store/store'
import { fetchApi, getByPath } from '../Functions'
import { DDCheckList, DDCheckListObject } from '../../types/DataDD/DataDD'
import { toast } from 'react-toastify'

export default function useSCheckList({ path }: { path: string }) {
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const { ChecklistsObject } = useSelector((state: RootState) => state.SequenceObject)
    const checklistGroupObject = getByPath(path, ChecklistsObject) as DDCheckListObject

    const updateItemStatus = async (item: DDCheckList) => {
        const response = await fetchApi(URLs.updateDD(item.ctUuid), {
            method: "PATCH",
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ statusId: item.statusId })
        });
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        return response.json();
    }

    const { mutate, isPending: isMutating, variables, mutateAsync } = useMutation({
        mutationFn: updateItemStatus,
        onSuccess: async (_, variables) => {
            dispatch(updateCheckListItem({
                completePath: `${path}${pathDelmiter}${variables.ctUuid}`,
                checklistItem: variables,
            }));
            toast.success("Checklist item updated successfully");
        },
        onError: (_, variables) => {
            if (!navigator.onLine) {
                dispatch(updateCheckListItem({
                    completePath: `${path}${pathDelmiter}${variables.ctUuid}`,
                    checklistItem: {
                        ...variables,
                        notSync: true,
                        previousStatusId: variables.previousStatusId ?? checklistGroupObject?.[variables.ctUuid]?.statusId,
                    }
                }))
                toast.success("Checklist item updated offline, will sync when online");
            }
        },
        networkMode: "offlineFirst"
    })

    const markPassed = (item: DDCheckList) => {
        const selectedItem = checklistGroupObject?.[item.ctUuid]
        if (selectedItem) return mutateAsync({
            ...selectedItem,
            statusId: Status.Passed,
            previousStatusId: selectedItem.previousStatusId ?? selectedItem.statusId,
        })
    }

    const markFailed = (item: DDCheckList) => {
        const selectedItem = checklistGroupObject?.[item.ctUuid]
        if (selectedItem) return mutateAsync({
            ...selectedItem,
            statusId: Status.Failed,
            previousStatusId: selectedItem.previousStatusId ?? selectedItem.statusId,
        })
    }

    const markNotApplicable = (item: DDCheckList) => {
        const selectedItem = checklistGroupObject?.[item.ctUuid]
        if (selectedItem) mutate({
            ...selectedItem,
            statusId: Status.NA,
            previousStatusId: selectedItem.previousStatusId ?? selectedItem.statusId,
        })
    }

    const reset = (item: DDCheckList) => {
        const selectedItem = checklistGroupObject?.[item.ctUuid]
        if (selectedItem) return mutateAsync({
            ...selectedItem,
            statusId: Status.Open,
            previousStatusId: selectedItem.previousStatusId ?? selectedItem.statusId,
        })
    }

    const markPending = (item: DDCheckList) => {
        const selectedItem = checklistGroupObject?.[item.ctUuid]
        if (selectedItem) return mutateAsync({
            ...selectedItem,
            statusId: Status.Pending,
            previousStatusId: selectedItem.previousStatusId ?? selectedItem.statusId,
        })
    }

    const navigateToPunchList = (item: DDCheckList) => {
        const isControlType = item.drilldownTypeId === 2 || item.drilldownTypeId === 4 || item.drilldownTypeId === 21;
        dispatch(setSelectedPath(path))
        dispatch(setSelectedDD01(item.dd01))
        dispatch(setSelectedDD02(isControlType ? item.dd03 : item.dd02))
        dispatch(setSelectedInspectionTask(item.taskDescription))
        dispatch(setSelectedModelNumber(item.modelNumber || null))
        navigate(`/PunchList/Form/CheckList/${item.ctUuid}`)
    }

    return {
        checkListObject: checklistGroupObject, markPassed, markFailed, navigateToPunchList, markNotApplicable, reset, markPending,
        showLoader: { isMutating, variables }
    }
}
